/**
 * Types, constantes et fabriques partagés du module Prestations.
 * Extraits de PrestationsModule.tsx lors de la refonte (lot 6) : le fichier dépassait 1280 lignes,
 * très au-dessus de la limite de 500. Aucune définition n'est modifiée ici — seulement déplacée.
 */

export type UnitType = 'unite' | 'm2' | 'ml' | 'surface';
// Public visé (2026-09-03, Emeni) : même vocabulaire que celui déjà utilisé pour les formules
// (client_type_scope) — une prestation "PRIVÉ" n'a rien à faire dans un devis Professionnel et vice-versa.
export type ClientTypeScope = 'TOUS' | 'PROFESSIONNEL' | 'PRIVÉ';

// `unit_price_percent` : prix de l'option exprimé en % du prix de la prestation visée (ex. 10 = 10 % du prix
// de la chaise). Prioritaire sur `unit_price_ttc` quand il est renseigné (> 0) ; recalculé à chaque lecture du
// catalogue, donc il suit les changements de prix de la prestation.
export interface OptionOverride { unit_price_ttc: number | null; default_duration_minutes: number | null; unit_price_percent?: number | null; }

export interface PrestationCatalogItem {
    id: string;
    label: string;
    unit_price_ttc: number;
    tax_rate: number;
    show_tva: boolean;
    unit_type: UnitType;
    surface_width: number;
    surface_length: number;
    default_duration_minutes: number;
    // Catégorie (2026-09-03, Emeni) : regroupe plusieurs prestations différentes sous un même seuil
    // d'éligibilité de formule (ex: "Assise" pour toutes les chaises, peu importe le tissu). Facultatif.
    category_id: string;
    // Public visé (2026-09-03, Emeni) : une prestation configurée pour les Pro (ex: nettoyage de mobilier de
    // restaurant) n'a pas sa place dans le catalogue proposé à un Particulier, et inversement. 'TOUS' = comme
    // avant, visible partout ; catalogue existant sans ce champ traité comme 'PROFESSIONNEL' au chargement
    // (Emeni : "j'ai configuré ce qui est pour les professionnels ici").
    client_type_scope: ClientTypeScope;
    // ─── Parcours guidé du simulateur (2026-09-09) ───────────────────────────────────────────────
    // Nature de la ligne : une prestation se choisit, une option se coche à la fin ("Désinfection",
    // "Hydrofuge"). Demande explicite d'Emeni : le choix se prend ici, sur la ligne, plutôt qu'en
    // rangeant la ligne dans une catégorie "Traitement" qui servirait de convention implicite.
    kind: 'prestation' | 'option';
    // L'objet auquel la prestation répond ("Canapé"), cf. SimulateurObjets.tsx.
    subcategory_id: string;
    // Les PRESTATIONS visées par une option, pas les objets : l'hydrofuge tissu ne va pas sur la chaise
    // en osier. Vide = l'option vaut pour tout.
    option_item_ids: string[];
    // Prix et temps propres à CHAQUE prestation visée (2026-10-09, Emeni) : la désinfection vapeur ne coûte
    // ni ne dure pareil sur une chaise et sur un lit, et une seule option suffit. Clé = id de la prestation
    // visée ; null = on garde le prix / le temps par défaut de l'option. Absent = rien ne change.
    option_overrides: Record<string, OptionOverride>;
    // Libellé court montré dans le parcours : « 3 places » sous la question « Combien de places ? », là
    // où le devis dit « Canapé droit 3 places ». Vide = on garde le libellé de facture.
    simulateur_label: string;
    // Certaines lignes sont internes et n'ont rien à faire sur le site public.
    simulateur_hidden: boolean;
    // ─── Photo de la prestation (2026-09-23) ──────────────────────────────────────────
    // L'image elle-même N'EST PAS ici : elle vit dans sa propre table côté serveur. Une photo en base64
    // dans ce JSON dépasserait les 200 Ko à partir desquels la lecture du catalogue est tronquée, et
    // emporterait tout le catalogue avec elle. Le catalogue ne garde que les deux cases d'affichage.
    image_show_demarche: boolean;
    image_show_simulateur: boolean;
    // ─── Lieu d'intervention (2026-09-24, Emeni) ──────────────────────────────────────
    // Où CETTE prestation peut se faire. Vide = comme l'établissement (le défaut, donc rien ne change
    // pour un catalogue existant). Une coupe ne se fait qu'au salon, un lissage peut se faire à domicile :
    // le réglage global de l'établissement ne suffisait pas à le dire.
    location_mode: '' | 'domicile' | 'entreprise' | 'les_deux';
}
export interface PrestationCategory { id: string; name: string; }

export const UNIT_TYPE_LABELS: Record<UnitType, string> = {
    unite: 'À l\'unité',
    m2: 'Au m²',
    // Demande d'Emeni (2026-09-06) : plusieurs de ses tarifs se comptent au mètre linéaire — « Banquette
    // Restaurant (au mètre linéaire) » par exemple.
    ml: 'Au mètre linéaire',
    surface: 'Surface (ex: 200x300)',
};

// Unité au singulier, pour les libellés au fil du texte (« Temps de travail par m² »).
export const UNIT_LABEL_COURT: Record<UnitType, string> = {
    unite: 'unité',
    m2: 'm²',
    ml: 'mètre linéaire',
    surface: 'pièce',
};

const nombreOuNull = (v: unknown): number | null =>
    v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Math.max(0, Number(v));

// Relit les surcharges d'une option depuis le catalogue enregistré (absentes d'un catalogue existant).
export const lireOverrides = (brut: unknown): Record<string, OptionOverride> => {
    const out: Record<string, OptionOverride> = {};
    if (!brut || typeof brut !== 'object') return out;
    for (const [id, o] of Object.entries(brut as Record<string, any>)) {
        const unit_price_ttc = nombreOuNull(o?.unit_price_ttc);
        const default_duration_minutes = nombreOuNull(o?.default_duration_minutes);
        const pct = nombreOuNull(o?.unit_price_percent);
        const unit_price_percent = pct !== null && pct > 0 ? pct : null;
        if (unit_price_ttc !== null || default_duration_minutes !== null || unit_price_percent !== null) out[id] = { unit_price_ttc, default_duration_minutes, unit_price_percent };
    }
    return out;
};

// Avant enregistrement : on écarte les surcharges vides et celles d'une prestation qui n'est plus visée.
export const ecrireOverrides = (overrides: Record<string, OptionOverride> | undefined, vises: string[] | undefined): Record<string, OptionOverride> => {
    const garde = lireOverrides(overrides);
    const ids = vises || [];
    return Object.fromEntries(Object.entries(garde).filter(([id]) => ids.includes(id)));
};

// Miroir de etendreOptionsParCible côté serveur (functions/api) : chaque surcharge d'une option devient une
// ligne virtuelle d'id `optionId::prestationId`, pour que devis et durées retrouvent le bon prix / temps.
export const etendreOptionsParCible = <T extends { id: string; label: string; kind?: string; unit_price_ttc: number; default_duration_minutes?: number }>(catalogue: T[]): T[] => {
    const parId = new Map(catalogue.map((it) => [String(it.id), it]));
    const virtuelles: T[] = [];
    for (const opt of catalogue as Array<T & { option_overrides?: Record<string, OptionOverride> }>) {
        if (opt.kind !== 'option' || !opt.option_overrides) continue;
        for (const [cibleId, o] of Object.entries(opt.option_overrides)) {
            const cible = parId.get(cibleId);
            const pct = nombreOuNull(o?.unit_price_percent);
            const prix = pct !== null && pct > 0
                ? Math.round((Number(cible.unit_price_ttc) || 0) * pct) / 100
                : nombreOuNull(o?.unit_price_ttc);
            const duree = nombreOuNull(o?.default_duration_minutes);
            if (!cible || (prix === null && duree === null)) continue;
            const { option_overrides: _ignore, ...base } = opt;
            virtuelles.push({
                ...base,
                id: `${opt.id}::${cibleId}`,
                label: `${opt.label} – ${cible.label}`,
                unit_price_ttc: prix ?? opt.unit_price_ttc,
                default_duration_minutes: duree ?? opt.default_duration_minutes,
                option_item_ids: [cibleId],
                virtual_of: opt.id,
            } as unknown as T);
        }
    }
    return virtuelles.length ? [...catalogue, ...virtuelles] : catalogue;
};

export const emptyItem = (defaultTva: number, scope: ClientTypeScope = 'TOUS'): PrestationCatalogItem => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: '',
    unit_price_ttc: 0,
    tax_rate: defaultTva,
    show_tva: true,
    unit_type: 'unite',
    surface_width: 0,
    surface_length: 0,
    default_duration_minutes: 30,
    category_id: '',
    client_type_scope: scope,
    kind: 'prestation',
    subcategory_id: '',
    option_item_ids: [],
    option_overrides: {},
    simulateur_label: '',
    simulateur_hidden: false,
    image_show_demarche: false,
    image_show_simulateur: false,
    location_mode: '',
});

// Bloc « simulateur » de la fiche d'une prestation : sa nature, son objet, son libellé court, et — pour
// une option — les prestations qu'elle vise. Séparé du reste du formulaire pour que la fiche reste
export interface FormulaItem {
    catalog_item_id: string;
    label: string;
    // Purement informatif (2026-09-02, demande d'Emeni) : signale que cette prestation doit revenir à
    // chaque passage, mais ne l'ajoute JAMAIS automatiquement au rendez-vous — l'établissement choisit
    // toujours lui-même ce qui compose chaque passage.
    required: boolean;
    discount_type: 'none' | 'percent' | 'amount';
    discount_value: number;
    free_occurrence_numbers: number[];
    // Plafond de l'offre en quantité : « désinfection offerte jusqu'à 100 assises ». 0 = sans limite.
    free_max_quantity: number;
}
// Seuil d'éligibilité PAR CATÉGORIE (2026-09-03, précision d'Emeni : « il en faut minimum 150 assise
// toute confondue » — plusieurs prestations différentes comptent ensemble). La formule est éligible dès
// qu'AU MOINS UN seuil est atteint sur le devis (OR entre catégories).
export interface CategoryThreshold { category_id: string; category_name: string; minimum_quantity: number; }
// 'any' = un seul seuil suffit (OU), 'all' = tous les seuils sont obligatoires à chaque passage (ET) —
// 2026-09-03, demande d'Emeni : « possibilité de choisir soit un seuil d'assise ou un seuil m2 ou les deux
// sont obligatoires ». Sans effet si un seul seuil est configuré.
export type ThresholdMode = 'any' | 'all';
// Nature du seuil (2026-09-08, demande d'Emeni) : 'quantite' = les seuils par catégorie historiques, qui
// ne parlent qu'aux professionnels comptant leurs chaises ; 'montant' = « Minimum X € TTC pour une
// prestation », le seul langage qui ait du sens pour un particulier. Jamais les deux à la fois.
export type ThresholdKind = 'quantite' | 'montant';
export interface PrestationFormula {
    id: string;
    name: string;
    description: string;
    engagement: boolean;
    occurrences_total: number;
    validity_months: number;
    items: FormulaItem[];
    threshold_kind: ThresholdKind;
    minimum_amount_ttc: number;
    // Passages libres : 12 mois sans nombre de passages imposé, minimum en montant ANNUEL.
    passages_libres: boolean;
    category_thresholds: CategoryThreshold[];
    category_thresholds_mode: ThresholdMode;
    client_type_scope: ClientTypeScope;
    is_active: boolean;
    /** Avantages commerciaux (remise par passage, options offertes…) ; {} = aucun. */
    avantages?: Record<string, any>;
}

export const emptyFormulaItem = (catalogItem: PrestationCatalogItem): FormulaItem => ({
    catalog_item_id: catalogItem.id, label: catalogItem.label, required: false,
    discount_type: 'none', discount_value: 0, free_occurrence_numbers: [], free_max_quantity: 0,
});

export const emptyFormula = (): Omit<PrestationFormula, 'id'> => ({
    name: '', description: '', engagement: false, occurrences_total: 6, validity_months: 12,
    items: [], threshold_kind: 'montant', minimum_amount_ttc: 0, passages_libres: false,
    category_thresholds: [], category_thresholds_mode: 'any', client_type_scope: 'TOUS', is_active: true, avantages: {},
});

// Vrai pour une formule à passages libres (booléen côté formulaire, 0/1 côté base).
export const estPassagesLibres = (x?: { passages_libres?: unknown } | null): boolean => !!Number((x as any)?.passages_libres);

export const ITEM_BENEFIT_LABEL = (it: FormulaItem): string => {
    const parts: string[] = [];
    if (it.free_occurrence_numbers.length > 0) parts.push(`occ. ${it.free_occurrence_numbers.join(', ')} offerte${it.free_occurrence_numbers.length > 1 ? 's' : ''}${it.free_max_quantity > 0 ? ` (jusqu'à ${it.free_max_quantity})` : ''}`);
    if (it.discount_type === 'percent' && it.discount_value > 0) parts.push(`-${it.discount_value}%`);
    if (it.discount_type === 'amount' && it.discount_value > 0) parts.push(`-${it.discount_value}€`);
    return parts.join(' · ');
};

